// Plain-language display labels for values the API returns as enums (spec §4.2).
// Pure functions; every unknown value falls back to the raw string so a new
// server-side value never renders blank. The run-status list itself lives in
// run-status.ts — this file only names it.
import type { RunStatus } from "./run-status";
import type { DlqBus } from "./api";

const RUN_STATUS_LABELS: Record<RunStatus, string> = {
  received: "Received",
  parsing: "Reading",
  transforming: "Preparing",
  looking_up: "Matching",
  delivering: "Writing",
  completed: "Synced",
  skipped: "Skipped",
  conflict: "Conflict",
  failed: "Failed",
};

/** A run status in plain words — "Writing", not `delivering`. */
export function runStatusLabel(status: string): string {
  return (RUN_STATUS_LABELS as Record<string, string>)[status] ?? status;
}

export type RunStatusGroupKey = "in-progress" | "synced" | "skipped" | "conflict" | "failed";

export interface RunStatusGroup {
  key: RunStatusGroupKey;
  label: string;
  /** The `status` value to send to GET /api/sync-log: one run status, or the
   *  `in-progress` group, which the API resolves to every in-flight status
   *  inside the query — before the page is cut — so nothing is trimmed away
   *  in the browser. */
  apiStatus: RunStatus | "in-progress";
}

/** The five groups the Activity status filter offers, in display order. */
export const RUN_STATUS_GROUPS: readonly RunStatusGroup[] = [
  { key: "in-progress", label: "In progress", apiStatus: "in-progress" },
  { key: "synced", label: "Synced", apiStatus: "completed" },
  { key: "skipped", label: "Skipped", apiStatus: "skipped" },
  { key: "conflict", label: "Conflict", apiStatus: "conflict" },
  { key: "failed", label: "Failed", apiStatus: "failed" },
];

/**
 * The filter group a run status belongs to.
 *
 * Anything unrecognised counts as in-progress rather than as its own bucket: a
 * status this build has not heard of is by definition not a settled outcome,
 * and reporting it as one would understate what still needs watching.
 */
export function runStatusGroup(status: string): RunStatusGroupKey {
  switch (status) {
    case "completed":
      return "synced";
    case "skipped":
      return "skipped";
    case "conflict":
      return "conflict";
    case "failed":
      return "failed";
    default:
      // received / parsing / transforming / looking_up / delivering — and any
      // status this build has not heard of, which is by definition not settled.
      return "in-progress";
  }
}

/** The group a filter key names, or undefined when nothing is selected. */
export function runStatusGroupFor(key: string | null | undefined): RunStatusGroup | undefined {
  return RUN_STATUS_GROUPS.find((g) => g.key === key);
}

/** The six DLQ-backed buses (mirrors `DlqBus` in api.ts, iterable). */
export const DLQ_BUSES: readonly DlqBus[] = [
  "webhook-events",
  "poll-events",
  "stream-events",
  "internal-events-profile",
  "internal-events-orders",
  "deliver",
];

const BUS_LABELS: Record<DlqBus, string> = {
  "webhook-events": "Incoming webhooks",
  "poll-events": "Scheduled polls",
  "stream-events": "Snowflake streams",
  "internal-events-profile": "Jump profile events",
  "internal-events-orders": "Jump order events",
  deliver: "Writes to destinations",
};

/** A queue in plain words — "Incoming webhooks", not `webhook-events`. */
export function busLabel(bus: string): string {
  return (BUS_LABELS as Record<string, string>)[bus] ?? bus;
}
